import Header from "@/components/hotel/Header";
import SubpageHero from "@/components/hotel/SubpageHero";
import Footer from "@/components/hotel/Footer";
import SEO from "@/components/SEO";
import { useCmsContent } from "@/hooks/useCmsContent";
import { Shield, Database, Eye, Scale, Share2, Clock, Lock, Cookie, UserCheck, Globe, RefreshCw, Mail } from "lucide-react";

const DatenschutzPage = () => {
  const { get } = useCmsContent();
  const cmsContent = get("datenschutz_content");
  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title="Datenschutzerklärung"
        description="Datenschutzerklärung des ESOS Hotel Quelle Bad Ragaz – Informationen zum Schutz Ihrer personenbezogenen Daten gemäss Schweizer Datenschutzgesetz und DSGVO."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Datenschutz", path: "/datenschutz" },
        ]}
      />
      <Header />
      <SubpageHero title="Datenschutzerklärung" subtitle="Data Protection & Privacy Policy" />

      {cmsContent ? (
        <section className="hotel-section bg-card">
          <div className="hotel-container max-w-4xl prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: cmsContent }} />
        </section>
      ) : (
      <section className="hotel-section bg-card">
        <div className="hotel-container max-w-4xl">
          {/* Intro */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="hotel-body text-muted-foreground">
              Esos Hotel Quelle nimmt den Schutz Ihrer persönlichen Daten sehr ernst. Diese
              Datenschutzerklärung erläutert, wie wir Ihre personenbezogenen Daten erheben,
              verarbeiten und schützen, wenn Sie eine Reservierung vornehmen oder unsere
              Website www.esoshotelquelle.com nutzen.
            </p>
            <p className="font-body text-sm text-muted-foreground mt-4">
              Wir verarbeiten personenbezogene Daten gemäss dem Schweizer Bundesgesetz
              über den Datenschutz (revDSG) und, soweit anwendbar, der
              EU-Datenschutzgrundverordnung (DSGVO).
            </p>
          </div>

          <div className="space-y-6">
            {/* 1. Controller */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Shield className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  1. Verantwortliche Stelle
                </h2>
              </div>
              <div className="space-y-1 font-body text-muted-foreground">
                <p className="font-semibold text-foreground">Esos Hotel Quelle</p>
                <p>Badstrasse 9a</p>
                <p>7310 Bad Ragaz</p>
                <p className="mt-2">UID-Nummer: CHE-114.450.031 MwSt</p>
                <p>Steuernummer: CHE-114.450.031</p>
                <p className="mt-2">E-Mail: reservation@esoshotelquelle.com</p>
                <p>Telefon: +41 81 302 11 13</p>
              </div>
            </div>

            {/* 2. Data We Collect */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Database className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  2. Erhobene personenbezogene Daten
                </h2>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Bei einer Reservierung oder Kontaktaufnahme können wir folgende Daten erheben:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Vor- und Nachname</li>
                  <li>Postanschrift</li>
                  <li>E-Mail-Adresse</li>
                  <li>Telefonnummer</li>
                  <li>Nationalität (sofern gesetzlich vorgeschrieben)</li>
                  <li>Reservierungsdetails (An-/Abreisedatum, Zimmertyp, besondere Wünsche)</li>
                  <li>Zahlungsbezogene Informationen (sicher über Drittanbieter verarbeitet; wir speichern keine vollständigen Kreditkartendaten)</li>
                  <li>IP-Adresse und technische Daten beim Besuch unserer Website</li>
                </ul>
              </div>
            </div>

            {/* 3. Purpose */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Eye className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  3. Zweck der Datenverarbeitung
                </h2>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Wir verarbeiten Ihre personenbezogenen Daten für folgende Zwecke:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Bearbeitung und Verwaltung von Reservierungen und Beherbergungsverträgen</li>
                  <li>Gästekommunikation vor, während und nach Ihrem Aufenthalt</li>
                  <li>Erfüllung gesetzlicher Pflichten (z. B. Meldepflichten)</li>
                  <li>Zahlungsabwicklung und Betrugsprävention</li>
                  <li>Verbesserung unserer Dienstleistungen und Website-Funktionalität</li>
                  <li>Beantwortung von Anfragen und Kundenservice</li>
                </ul>
              </div>
            </div>

            {/* 4. Legal Basis */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Scale className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  4. Rechtsgrundlage der Verarbeitung
                </h2>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Ihre personenbezogenen Daten werden verarbeitet auf Grundlage von:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Vertragserfüllung</strong> (Reservierung und Beherbergungsleistungen)</li>
                  <li><strong className="text-foreground">Gesetzliche Verpflichtungen</strong> nach Schweizer Recht</li>
                  <li><strong className="text-foreground">Berechtigte Interessen</strong> (z. B. Serviceverbesserung, Sicherheit)</li>
                  <li><strong className="text-foreground">Einwilligung</strong>, sofern erforderlich (z. B. Marketing-Kommunikation)</li>
                </ul>
              </div>
            </div>

            {/* 5. Third Parties */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Share2 className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  5. Weitergabe an Dritte
                </h2>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Ihre Daten werden nur bei Bedarf an vertrauenswürdige Dritte weitergegeben:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Reservierungs- und Buchungssystem-Anbieter</li>
                  <li>Zahlungsdienstleister</li>
                  <li>IT- und Hosting-Dienstleister</li>
                  <li>Behörden, soweit gesetzlich vorgeschrieben</li>
                </ul>
                <p>
                  Alle Drittanbieter sind vertraglich verpflichtet, Ihre Daten zu schützen und
                  nur für die vereinbarten Zwecke zu verarbeiten.
                </p>
              </div>
            </div>

            {/* 6. Storage & Retention */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  6. Datenspeicherung und Aufbewahrung
                </h2>
              </div>
              <p className="font-body text-muted-foreground leading-relaxed">
                Wir speichern personenbezogene Daten nur so lange, wie es für die oben
                genannten Zwecke erforderlich ist oder gesetzlich vorgeschrieben. Buchungs-
                und Buchhaltungsdaten werden gemäss den schweizerischen gesetzlichen
                Aufbewahrungsfristen aufbewahrt.
              </p>
            </div>

            {/* 7. Data Security */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Lock className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  7. Datensicherheit
                </h2>
              </div>
              <p className="font-body text-muted-foreground leading-relaxed">
                Wir setzen geeignete technische und organisatorische Massnahmen ein, um Ihre
                personenbezogenen Daten gegen unbefugten Zugriff, Verlust, Missbrauch oder
                Veränderung zu schützen. Unsere Website verwendet SSL-Verschlüsselung für
                eine sichere Datenübertragung.
              </p>
            </div>

            {/* 8. Cookies */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Cookie className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  8. Cookies und Website-Analyse
                </h2>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Unsere Website kann Cookies und ähnliche Technologien verwenden, um die
                  ordnungsgemässe Funktionalität sicherzustellen und die Benutzererfahrung zu
                  verbessern. Sie können Cookies über Ihre Browsereinstellungen verwalten oder
                  deaktivieren.
                </p>
              </div>
            </div>

            {/* 9. Your Rights */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <UserCheck className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  9. Ihre Rechte
                </h2>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Sie haben das Recht:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Auskunft über Ihre personenbezogenen Daten zu verlangen</li>
                  <li>Die Berichtigung oder Löschung Ihrer Daten zu verlangen</li>
                  <li>Die Verarbeitung einzuschränken oder ihr zu widersprechen</li>
                  <li>Ihre Einwilligung jederzeit zu widerrufen (sofern zutreffend)</li>
                  <li>Datenübertragbarkeit zu verlangen (sofern zutreffend)</li>
                </ul>
                <p>
                  Anfragen können per E-Mail an die oben genannte Kontaktadresse gerichtet werden.
                </p>
              </div>
            </div>

            {/* 10. International Transfers */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Globe className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  10. Internationale Datenübertragungen
                </h2>
              </div>
              <p className="font-body text-muted-foreground leading-relaxed">
                Wenn personenbezogene Daten ausserhalb der Schweiz oder des EU-/EWR-Raums
                übertragen werden, stellen wir durch geeignete Schutzmassnahmen ein
                angemessenes Datenschutzniveau sicher.
              </p>
            </div>

            {/* 11. Changes */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <RefreshCw className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  11. Änderungen dieser Richtlinie
                </h2>
              </div>
              <p className="font-body text-muted-foreground leading-relaxed">
                Wir behalten uns das Recht vor, diese Datenschutzerklärung jederzeit zu
                aktualisieren. Es gilt die jeweils auf unserer Website veröffentlichte aktuelle
                Fassung.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Mail className="w-5 h-5 text-accent" />
                </div>
                <h2 className="font-display text-2xl font-semibold text-foreground">
                  Kontakt für Datenschutzfragen
                </h2>
              </div>
              <div className="bg-card p-5 rounded-lg font-body text-muted-foreground space-y-1">
                <p className="font-semibold text-foreground">Esos Hotel Quelle</p>
                <p>Badstrasse 9a, 7310 Bad Ragaz</p>
                <p>E-Mail: reservation@esoshotelquelle.com</p>
                <p>Telefon: +41 81 302 11 13</p>
              </div>
            </div>

            {/* Last updated */}
            <div className="bg-muted/50 p-6 rounded-lg text-center">
              <p className="font-body text-sm text-muted-foreground">
                <span className="font-semibold">Letzte Aktualisierung:</span> Februar 2026
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      <Footer />
    </main>
  );
};

export default DatenschutzPage;
